import { slate } from '@radix-ui/colors';
import CopyToClipboard from 'react-copy-to-clipboard';

import { SVGIcon } from '@paid-ui/icons';
import { Flex, Grid, styled } from '@paid-ui/ui';

export const DialogHeader = styled('header', {
  'position': 'sticky',
  'top': 0,
  'width': '100%',
  'flexShrink': 0,
  'display': 'flex',
  'flexDirection': 'column',
  'gap': '12px',
  'padding': '18px 20px',
  'borderBottom': '1px solid transparent',
  'backdropFilter': 'none',
  'backgroundColor': '$bg',
  'transition': 'all 250ms ease',
  'zIndex': 10,

  '&:empty': {
    display: 'none',
  },

  '@sm': {
    padding: '18px 30px',
    borderTopLeftRadius: 0,
    borderTopRightRadius: '16px',
  },
});

export const DialogCloseButton = styled('button', {
  'all': 'unset',
  'boxSizing': 'border-box',
  'position': 'absolute',
  'size': '24px',
  'padding': '3px',
  'cursor': 'pointer',
  'right': '20px',
  'color': '$fgSecondary',
  'transition': 'color 100ms',
  'flexShrink': 0,

  '@sm': {
    right: '30px',
  },
  '@hover': {
    '&:hover': {
      color: '$blue',
    },
  },
  'variants': {
    compact: {
      true: {
        right: '20px',
      },
    },
  },
});

export const DialogCloseIcon = styled(SVGIcon, {
  size: '18px',
});

export const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  overflow: 'hidden auto',
});

export const Header = styled('header', {
  'position': 'sticky',
  'top': 0,
  'width': '100%',
  'flexShrink': 0,
  'padding': '18px 20px',
  'backgroundColor': '$bg',
  'transition': 'all 250ms ease',
  'zIndex': 10,
  'display': 'flex',
  'flexDirection': 'column',
  'gap': '14px',
  'alignItems': 'center',
  'justifyContent': 'center',

  '@sm': {
    padding: '18px 30px',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
  },

  'variants': {
    bordered: {
      true: {
        borderBottom: '1px solid $colors$midGrey',
      },
    },
    compact: {
      true: {
        padding: '18px 20px',
      },
    },
  },
});

export const Content = styled(Flex, {
  flex: 1,
  padding: '18px 30px',
  flexDirection: 'column',
  gap: '24px',
  width: '100%',
  variants: {
    compact: {
      true: {
        padding: '18px 20px',
      },
    },
  },
});

export const ApplyAccountContainer = styled(Flex, {
  padding: '14px 20px',
  backgroundColor: '$fg',
  color: '$bg',
  fontSize: '14px',
  fontWeight: '$medium',
  justifyContent: 'space-between',
  alignItems: 'center',
  variants: {
    hidden: {
      true: {
        display: 'none',
      },
    },
  },
});

export const ClaimHeaderContainer = styled(Flex, {
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const ClaimHeader = styled('div', {
  fontSize: '16px',
  fontWeight: '$medium',
});

export const ClaimHeaderExtra = styled('button', {
  'all': 'unset',
  'boxSizing': 'border-box',
  'color': '$blue',
  'fontSize': '14px',
  'lineHeight': '18px',
  'fontWeight': '$medium',
  'transition': 'color 250ms ease',
  'cursor': 'pointer',

  '@hover': {
    '&:hover': {
      color: '$hoverBlue',
    },
  },

  'variants': {
    hidden: {
      true: {
        display: 'none',
      },
    },
  },
});

export const Warnings = styled('p', {
  flex: 1,
  display: 'flex',
  gap: '6px',
  padding: '20px',
  backgroundColor: 'rgba(255, 0, 115, 0.05)',
  border: '1px solid $colors$pink',
  borderRadius: '6px',
  color: '$pink',
  fontSize: '14px',
  lineHeight: '18px',
  fontWeight: '$medium',
  variants: {
    hidden: {
      true: {
        display: 'none',
      },
    },
  },
});

export const CopyButton = styled(CopyToClipboard, {
  'all': 'unset',
  'boxSizing': 'border-box',
  'size': '24px',
  'padding': '3px',
  'cursor': 'pointer',
  'color': '$fg',
  'transition': 'color 150ms ease',

  '@hover': {
    '&:hover': {
      color: '$blue',
    },
  },
});

export const CopyFlex = styled(Flex, {
  'position': 'relative',
  'gap': '10px',
  'alignItems': 'center',
  'justifyContent': 'space-between',
  '@sm': {
    [`& ${CopyButton}`]: {
      visibility: 'hidden',
    },
    '&:hover': {
      [`& ${CopyButton}`]: {
        visibility: 'visible',
      },
    },
  },
});

export const CheckIcon = styled(SVGIcon, {
  size: '32px',
});

export const DialogContainer = styled('form', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  overflow: 'hidden auto',
});

export const EditProfileContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  overflow: 'hidden auto',
});

export const AutoPayText = styled('span', {
  fontSize: '14px',
  color: '$pink',
});

export const HowToPayText = styled('span', {
  fontSize: '16px',
  fontWeight: '$medium',
  variants: {
    subtext: {
      true: {
        fontSize: '14px',
        color: '$darkGrey',
        fontWeight: '$normal',
      },
    },
  },
});

export const DialogTitle = styled('h2', {
  color: '$fg',
  fontSize: '22px',
  fontWeight: '$semibold',
  lineHeight: '27px',
  ellipsis: 'calc(100% - 24px)',
});

export const CustomForm = styled('form', {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

export const BottomStickyContainer = styled('footer', {
  'position': 'sticky',
  'bottom': 0,
  'flexShrink': 0,
  'padding': '18px 20px',
  'display': 'flex',
  'gap': '12px',
  'alignItems': 'center',
  'justifyContent': 'flex-end',
  'backgroundColor': '$bg',
  'borderTop': '1px solid $colors$lightGrey',
  'zIndex': '$navbar',
  '@sm': {
    borderBottomLeftRadius: '16px',
    borderBottomRightRadius: '16px',
    padding: '18px 30px',
  },
  'variants': {
    hidden: {
      true: {
        display: 'none',
      },
    },
    compact: {
      true: {
        padding: '18px 20px',
      },
    },
  },
});

export const InputGrid = styled('div', {
  'display': 'grid',
  'width': '100%',
  'position': 'relative',
  'gridTemplateColumns': 'repeat(1, minmax(0, 1fr))',
  'gap': '36px 0',

  '@sm': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '36px 20px',
  },

  'variants': {
    hidden: {
      true: {
        display: 'none',
      },
    },
  },
});

export const InputLabel = styled('div', {
  fontSize: '22px',
  lineHeight: '27px',
  fontWeight: '$semibold',
  flexShrink: 0,
  variants: {
    desc: {
      true: {
        fontSize: '16px',
        color: '$darkGrey',
        lineHeight: '22px',
        fontWeight: '$normal',
      },
    },
  },
});

export const CompanySignatoriesContainer = styled(Flex, {
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
  listStyle: 'none',
});

export const AddSignatoriesButton = styled('button', {
  'all': 'unset',
  'marginLeft': 'auto',
  'marginBottom': '75px',
  'padding': '8px 10px',
  'display': 'flex',
  'alignItems': 'center',
  'color': '$blue',
  'userSelect': 'none',
  'cursor': 'pointer',
  'gap': '6px',

  '&:disabled': {
    color: slate.slate8,
    pointerEvents: 'none',
  },
  'variants': {
    hidden: {
      true: {
        display: 'none',
      },
    },
  },
});

export const CompanySignatoriesCardMilestoneOne = styled(Grid, {
  'alignItems': 'start',
  'gridTemplateAreas': '"firstName" "lastName" "role" "email"',
  'gridTemplateColumns': '1fr',
  'position': 'relative',
  'width': '100%',
  'backgroundColor': '$lightGrey',
  'border': '1px solid $colors$midGrey',
  'borderRadius': '6px',
  'padding': '10px',
  'gap': '14px 10px',
  '@sm': {
    alignItems: 'flex-start',
    gridTemplateAreas: '"firstName lastName role" "email email email"',
    gridTemplateColumns: '160px 160px 1fr',
    gap: '10px',
  },
});

export const CompanySignatoriesCard = styled(Grid, {
  'alignItems': 'start',
  'gridTemplateAreas': '"checkbox firstName deleteButton" ". lastName ." ". role ." ". email ."',
  'gridTemplateColumns': '24px 1fr 24px',
  'position': 'relative',
  'width': '100%',
  'backgroundColor': '$lightGrey',
  'border': '1px solid $colors$midGrey',
  'borderRadius': '6px',
  'padding': '10px',
  'gap': '14px 10px',
  '@sm': {
    alignItems: 'center',
    gridTemplateAreas: '"checkbox firstName lastName role deleteButton" ". email email email ."',
    gridTemplateColumns: '24px 160px 160px 1fr 24px',
    gap: '10px',
  },
});

export const IndividualTrusteeCardContainer = styled(Grid, {
  gap: '8px',
});

export const IndividualTrusteeCard = styled(Grid, {
  'alignItems': 'center',
  'gridTemplateAreas': '"firstName deleteButton" "lastName ." "email ."',
  'gridTemplateColumns': '1fr 24px',
  'position': 'relative',
  'width': '100%',
  'backgroundColor': '$lightGrey',
  'border': '1px solid $colors$midGrey',
  'borderRadius': '6px',
  'padding': '10px',
  'gap': '14px 10px',
  '@sm': {
    gridTemplateAreas: '"firstName lastName deleteButton" "email email ."',
    gridTemplateColumns: '1fr 1fr 24px',
    gap: '10px',
  },
});

export const AdditionalOwnerCardContainer = styled(Grid, {
  gap: '10px',
  position: 'relative',
  width: '100%',
  padding: '10px',
  backgroundColor: '$lightGrey',
  border: '1px solid $colors$midGrey',
  borderRadius: '6px',
});

export const AdditionalOwnerAddress = styled(Grid, {
  gap: '10px',
  gridTemplateColumns: '1fr 24px',
});

export const AdditionalOwnerCard = styled(Grid, {
  'alignItems': 'center',
  'gridTemplateAreas': '"firstName deleteButton" "lastName ." "email ."',
  'gridTemplateColumns': '1fr 24px',
  'gap': '14px 10px',
  '@sm': {
    gridTemplateAreas: '"firstName lastName deleteButton" "email email ."',
    gridTemplateColumns: '1fr 1fr 24px',
    gap: '10px',
  },
});

export const SignatoriesLabel = styled('span', {
  fontSize: '16px',
  fontWeight: '$medium',
  lineHeight: '22px',
  variants: {
    sub: {
      true: {
        color: '$darkGrey',
        fontWeight: '$normal',
      },
    },
  },
});

export const DeleteButton = styled('button', {
  gridArea: 'deleteButton',
  all: 'unset',
  boxSizing: 'border-box',
  size: '24px',
  padding: '1px',
  cursor: 'pointer',

  variants: {
    hidden: {
      true: {
        display: 'none',
      },
    },
    disabled: {
      true: {
        pointerEvents: 'none',
      },
    },
  },
});

export const DeleteIcon = styled(SVGIcon, {
  'size': '22px',
  'color': '$fgSecondary',

  '&:hover': {
    color: '$pink',
  },
  'variants': {
    disabled: {
      true: {
        color: slate.slate8,
      },
    },
  },
});

export const OverSecuredText = styled('span', {
  color: '$darkGrey',
  fontSize: '14px',
  lineHeight: '18px',
});
